import React, { useState } from 'react';
import { finishReceipt } from 'services/api/receipt';
import { SCREEN_TYPES } from 'utils/constants';
import {
  useAppStore,
  useAuthStore,
  useProductStore,
  useReceiptStore,
  useTrackingStore,
} from 'store';
import { toast } from 'sonner';
import { Button } from 'components/ui/Button';
import { CardTitle } from 'components/ui/card';
import { resetAllProductRelatedStores } from '../utils';

export default function ConfirmClose() {
  const [loading, setLoading] = useState(false);
  const userId = useAuthStore((state) => state.user.id);
  const { stationId } = useAuthStore();
  const { setCurrentScreen } = useAppStore();
  const { receiptData, setReceiptData } = useReceiptStore();
  const { setCurrentProduct } = useProductStore();
  const { setLastTrackingId, pendingTrackingId, setPendingTrackingId, setCurrentTrackingId } =
    useTrackingStore();

  const onConfirm = () => {
    setReceiptData(null);
    setCurrentProduct(null);
    setLastTrackingId(null);
    setPendingTrackingId(null);
    setCurrentTrackingId('');
    setCurrentScreen(SCREEN_TYPES.SCAN_BOX);
  };

  const onCancel = () => setCurrentScreen(SCREEN_TYPES.SCAN_BOX);

  const handleClose = async (): Promise<void> => {
    setLoading(true);

    try {
      const transactionId = receiptData?.transaction_id || receiptData?.receipt?.transaction_id;
      const result = await finishReceipt(transactionId, userId, stationId);

      if (result?.responseState === 'confirmed') {
        toast.success('Receipt closed successfully');
        setTimeout(() => {
          if (pendingTrackingId) {
            // If there's a pending tracking ID, go back to SCAN_BOX and let user scan it
            setReceiptData(null);
            setCurrentProduct(null);
            setLastTrackingId(null);
            setPendingTrackingId(null);
            setCurrentScreen(SCREEN_TYPES.SCAN_BOX);
            setCurrentTrackingId('');
          } else {
            onConfirm();
            resetAllProductRelatedStores();
          }
        }, 1000);
      } else {
        toast.error(result?.responseMsg || 'Failed to close receipt');
        setLoading(false);
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
      setLoading(false);
    }
  };

  const expectedTotal =
    receiptData?.lines?.filter((line) => line && line.expected_ind === 'y').length || 0;
  const actualScanned =
    receiptData?.lines?.filter((line) => line && line.receipt_ind === 'y').length || 0;
  const expectedScanned =
    receiptData?.lines?.filter(
      (line) => line && line.expected_ind === 'y' && line.receipt_ind === 'y'
    ).length || 0;
  const unexpectedScanned =
    receiptData?.lines?.filter(
      (line) => line && line.expected_ind === 'n' && line.receipt_ind === 'y'
    ).length || 0;
  const fraudScanned = 0;

  return (
    <div className="space-y-4">
      <CardTitle className="mb-1 flex items-center justify-center gap-3 text-center text-xl font-extrabold tracking-tight text-slate-900">
        CONFIRM CLOSE
      </CardTitle>

      <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-base">
        <span className="mr-2 font-semibold text-slate-700">Tracking #</span>
        <span className="break-all font-semibold text-slate-900">
          {receiptData?.tracking_id || receiptData?.receipt?.tracking_id}
        </span>
      </div>

      <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
          Confirm All Items Scanned
        </h3>

        <div className="mb-2 flex items-center justify-between text-[15px] font-medium text-slate-700">
          <span className="pr-3">Expected Total:</span>
          <span className="font-bold text-slate-900">{expectedTotal}</span>
        </div>

        <div className="flex items-center justify-between text-[15px] font-medium text-slate-700">
          <span className="pr-3">Actual Scanned:</span>
          <span className="font-bold text-slate-900">{actualScanned}</span>
        </div>
      </div>

      <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
          Scanned Breakdown
        </h3>

        <div className="mb-2 flex items-center justify-between text-[15px] font-medium text-slate-700">
          <span className="pr-3">Expected Scanned:</span>
          <span className="font-bold text-slate-900">{expectedScanned}</span>
        </div>

        <div className="mb-2 flex items-center justify-between text-[15px] font-medium text-slate-700">
          <span className="pr-3">Unexpected Scanned:</span>
          <span className="font-bold text-slate-900">{unexpectedScanned}</span>
        </div>

        <div className="flex items-center justify-between text-[15px] font-medium text-slate-700">
          <span className="pr-3">Fraud Scanned:</span>
          <span className="font-bold text-slate-900">{fraudScanned}</span>
        </div>
      </div>

      <div className="buttons-container">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
          className="h-12 flex-1 rounded-xl border-2 border-red-600 text-base font-semibold text-red-600 hover:bg-red-50 hover:text-red-700"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleClose}
          disabled={loading}
          className="h-12 flex-1 rounded-xl text-base font-semibold shadow-sm"
        >
          {loading ? 'Closing...' : 'CLOSE'}
        </Button>
      </div>
    </div>
  );
}
